import type { Sex, GoalType } from "./types";
import { eventPacing } from "./event-goal";
import { ABSOLUTE_MIN_KCAL } from "./safety-floors";

export type ActivityLevel = "sedentary" | "light" | "moderate" | "high";

/** Total-energy multipliers on BMR, mapped from workouts/week
 *  (0 → sedentary, 1–2 → light, 3–5 → moderate, 6+ → high). Undefined keeps
 *  the historical lightly-active 1.4 so accounts created before this question
 *  are unchanged. */
export const ACTIVITY_MULTIPLIER: Record<ActivityLevel, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  high: 1.725,
};

export interface OnboardingProfile {
  sex: Sex;
  age: number;        // years
  height_cm: number;
  weight_kg: number;
  goal_type: GoalType;
  /** From "workouts per week"; scales the maintenance estimate. */
  activity?: ActivityLevel;
}

/**
 * Initial calorie targets for a fresh account.
 *
 * Mifflin-St Jeor BMR × 1.4 (lightly-active default) gives the starting
 * maintenance estimate; the app's measured-TDEE engine replaces it with real
 * numbers as weight data accumulates. Budget offset per goal:
 * weight_loss/cut −500, maintain 0, bulk/weight_gain +300 — unless a dated
 * occasion is given, in which case the offset is paced to that date instead
 * (clamped safe in shared/event-goal.ts; a null pacing falls back to the
 * fixed numbers, so passing no event changes nothing).
 * Protein target: 1.8 g per kg of body weight.
 */
export function initialTargets(
  p: OnboardingProfile,
  event?: { event_date: string; today: string; target_weight_kg: number } | null,
): {
  maintenance_calories: number;
  daily_calories: number;
  protein_g: number;
} {
  const s = p.sex === "male" ? 5 : p.sex === "female" ? -161 : -78; // midpoint for "other"
  const bmr = 10 * p.weight_kg + 6.25 * p.height_cm - 5 * p.age + s;
  const mult = p.activity ? ACTIVITY_MULTIPLIER[p.activity] : 1.4;
  const maintenance = Math.round((bmr * mult) / 10) * 10;
  const pacing = event
    ? eventPacing({
        weight_kg: p.weight_kg,
        target_weight_kg: event.target_weight_kg,
        event_date: event.event_date,
        today: event.today,
        goal_type: p.goal_type,
      })
    : null;
  const offset = pacing
    ? pacing.offset_kcal
    : p.goal_type === "weight_loss" || p.goal_type === "cut" ? -500 :
      p.goal_type === "bulk" || p.goal_type === "weight_gain" ? 300 : 0;
  // Never go below a safe floor.
  const daily = Math.max(ABSOLUTE_MIN_KCAL, maintenance + offset);
  return {
    maintenance_calories: maintenance,
    daily_calories: daily,
    protein_g: Math.round(p.weight_kg * 1.8),
  };
}
